/**
 * The first home view after a public-template signup: a preview of the canvas
 * that motivated the signup, and one action that copies it into the user's
 * private workspace and opens it.
 *
 * Visual language is the home onboarding dialog's (`_co_home_onb_*` over the
 * shared `_co_onb` tokens) — same scrim, card, staircase and rust pill, since
 * this replaces that dialog for the one view it owns. The copy chain is the
 * public viewer's, verbatim: POST copy → poll the import to terminal →
 * navigate. Only the navigation differs — we are already on the app origin.
 */

import { useCopyPublishedCanvas } from "@/api/canvas";
import { ApiError } from "@/api/utils";
import { ROUTES } from "@/constants/routes";
import { addToWorkspaceErrorMessage } from "@/public-canvas/addToWorkspaceErrorMessage";
import { pollImportedCanvasTerminal } from "@/public-canvas/pollImportedCanvasTerminal";
import * as analytics from "@/utils/analytics";
import { route } from "@/utils/route";
import { useThemeContext } from "@cohesive/ui-components";
import { isAxiosError } from "axios";
import { Compass } from "lucide-react";
import { memo, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  useTemplateCopyPromptContext,
  type TemplateCopyPrompt,
} from "./TemplateCopyPromptContext";
import { PROMPT_DIALOG_STYLES } from "./promptDialogStyles";
import {
  buildPublicFileUrl,
  buildTemplateOgImageUrl,
} from "./publicTemplateUrls";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type CopyStatus = "idle" | "copying" | "error" | "unavailable";

export const TemplateCopyPromptDialog = memo(
  function TemplateCopyPromptDialog() {
    const navigate = useNavigate();
    const { mode } = useThemeContext();
    const context = useTemplateCopyPromptContext();
    const copyPublished = useCopyPublishedCanvas();

    const [status, setStatus] = useState<CopyStatus>("idle");
    const [errorMessage, setErrorMessage] = useState("");
    const [imageFailed, setImageFailed] = useState(false);
    const [closed, setClosed] = useState(false);
    /**
     * The context drops the prompt the moment it resolves; the copy and the
     * dead-source notice both outlive that, so they hold their own copy.
     */
    const [heldPrompt, setHeldPrompt] = useState<TemplateCopyPrompt | null>(
      null,
    );
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => () => abortRef.current?.abort(), []);

    const prompt = context?.prompt ?? heldPrompt;
    const view = context?.view ?? "none";
    // `copying` outlives the resolve that precedes the navigation, and
    // `unavailable` outlives the one that retires a dead source — both hold
    // the card in place after the context has moved on.
    const shouldShow =
      !closed &&
      !!prompt &&
      (view === "dialog" || status === "copying" || status === "unavailable");

    const shownRef = useRef(false);
    useEffect(() => {
      if (!shouldShow || shownRef.current || !prompt) return;
      shownRef.current = true;
      analytics.trackTemplatePromptShown({ kind: prompt.kind, id: prompt.id });
    }, [shouldShow, prompt]);

    const dialogRef = useRef<HTMLDivElement | null>(null);
    const previouslyFocusedRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
      if (!shouldShow) return;

      previouslyFocusedRef.current =
        document.activeElement as HTMLElement | null;

      const node = dialogRef.current;
      if (node) {
        const first = node.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        first?.focus();
      }

      const onKeyDown = (e: KeyboardEvent) => {
        // Non-dismissible, like the onboarding dialog: Escape does nothing.
        if (e.key === "Tab" && node) {
          const focusables = Array.from(
            node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
          ).filter((el) => !el.hasAttribute("data-co-onb-trap"));
          if (focusables.length === 0) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;
          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };

      document.addEventListener("keydown", onKeyDown);
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      return () => {
        document.removeEventListener("keydown", onKeyDown);
        document.body.style.overflow = prevOverflow;
        previouslyFocusedRef.current?.focus?.();
      };
    }, [shouldShow]);

    if (!shouldShow || !prompt || !context) return null;

    const { authorName, title, kind } = prompt;
    const isCanvas = kind === "canvas";
    const isCopying = status === "copying";
    const isUnavailable = status === "unavailable";

    const fail = (message: string, reason: string) => {
      setErrorMessage(message);
      setStatus("error");
      analytics.trackTemplateCopyFailed({ kind, id: prompt.id, reason });
      // No resolve(): the prompt stays pending and survives a reload.
    };

    const handleCopy = async () => {
      if (status === "copying") return;
      setHeldPrompt(prompt);
      setStatus("copying");
      try {
        const shell = await copyPublished.mutateAsync({
          canvasId: prompt.id,
          // No workspace id — the endpoint defaults to the private workspace.
          workspaceId: undefined,
        });

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const importStatus = await pollImportedCanvasTerminal(
          shell.id,
          controller.signal,
        );
        if (controller.signal.aborted) return;

        if (importStatus === "ready") {
          analytics.trackTemplateCopyCompleted({ kind, id: prompt.id });
          context.resolve();
          navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: shell.id }));
          return; // hold "Copying…" through the navigation
        }

        fail(
          importStatus === "failed"
            ? "Failed to add canvas. Please try again."
            : "This is taking longer than expected. Please try again.",
          importStatus === "failed" ? "import_failed" : "timeout",
        );
      } catch (error) {
        // The api instances' response interceptor re-throws failures as
        // `ApiError`; a raw `AxiosError` reaches here only without it.
        const status =
          error instanceof ApiError
            ? error.status
            : isAxiosError(error)
              ? error.response?.status
              : undefined;
        if (status === 404) {
          analytics.trackTemplateCopyFailed({
            kind,
            id: prompt.id,
            reason: "unavailable",
          });
          setStatus("unavailable");
          context.resolve(); // a dead source must not prompt again
          return;
        }
        fail(addToWorkspaceErrorMessage(error), "error");
      }
    };

    const handleView = () => {
      analytics.trackTemplateCopyCompleted({ kind, id: prompt.id });
      context.resolve();
      // The anchor's own navigation proceeds.
    };

    const heading = title || (isCanvas ? "Untitled canvas" : "Untitled file");
    const lede = isCanvas
      ? "Copy it into your workspace to explore, edit, and make it yours."
      : "Open the file that brought you here.";
    const copyLabel = authorName
      ? `Copy ${authorName}'s template`
      : "Copy this template";
    const viewLabel = title ? `View ${title}` : "View file";

    const showOgImage = isCanvas && !imageFailed;

    return createPortal(
      <div className={mode === "dark" ? "dark" : undefined}>
        <style>{PROMPT_DIALOG_STYLES}</style>
        <style>{DIALOG_STYLES}</style>
        {/* Inert scrim — the user picks one of the explicit actions. */}
        <div className="_co_onb _co_home_onb_scrim" role="presentation">
          <div
            ref={dialogRef}
            className="_co_onb _co_home_onb_card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="_co_tpl_prompt_heading"
            aria-describedby="_co_tpl_prompt_lede"
            data-testid="template-copy-prompt-dialog"
          >
            <div
              className="_co_onb_settle_1 _co_tpl_prompt_preview"
              data-testid="template-copy-prompt-preview"
            >
              {showOgImage ? (
                <img
                  src={buildTemplateOgImageUrl(prompt.id)}
                  alt=""
                  width={1200}
                  height={630}
                  onError={() => setImageFailed(true)}
                />
              ) : (
                <div className="_co_tpl_prompt_preview_fallback">
                  <Compass
                    className="size-10"
                    style={{ color: "#B86E49" }}
                    strokeWidth={1.75}
                  />
                </div>
              )}
            </div>

            <div className="_co_home_onb_content">
              <div className="_co_onb_settle_2 _co_home_onb_eyebrow">
                <span
                  className="_co_onb_dot"
                  style={{
                    width: "var(--co-onb-dot)",
                    height: "var(--co-onb-dot)",
                    borderRadius: "50%",
                    background: "var(--co-rust)",
                    display: "inline-block",
                  }}
                />
                {isCanvas ? "Your template" : "Your file"}
              </div>

              <h1
                id="_co_tpl_prompt_heading"
                className="_co_onb_settle_3 _co_home_onb_heading"
              >
                {heading}
              </h1>

              {authorName ? (
                <p className="_co_onb_settle_3 _co_tpl_prompt_author">
                  by {authorName}
                </p>
              ) : null}

              <p
                id="_co_tpl_prompt_lede"
                className="_co_onb_settle_4 _co_home_onb_lede"
              >
                {isUnavailable ? "This template is no longer available." : lede}
              </p>

              {status === "error" ? (
                <p
                  role="alert"
                  className="_co_tpl_prompt_error"
                  data-testid="template-copy-prompt-error"
                >
                  {errorMessage}
                </p>
              ) : null}

              <div className="_co_onb_settle_4 _co_home_onb_actions">
                {isUnavailable ? (
                  <button
                    type="button"
                    className="_co_onb_finish _co_home_onb_primary"
                    data-testid="template-copy-prompt-primary"
                    onClick={() => {
                      context.resolve();
                      setClosed(true);
                    }}
                    autoFocus
                  >
                    Continue
                  </button>
                ) : isCanvas ? (
                  <button
                    type="button"
                    className="_co_onb_finish _co_home_onb_primary"
                    data-testid="template-copy-prompt-primary"
                    // `alt` isn't a typed <button> prop but renders to the DOM
                    // and is captured by Sentry replay click selectors.
                    {...({ alt: "template-prompt-copy" } as Record<
                      string,
                      string
                    >)}
                    onClick={() => void handleCopy()}
                    disabled={isCopying}
                    autoFocus
                  >
                    {isCopying ? (
                      <>
                        <Spinner />
                        Copying…
                      </>
                    ) : status === "error" ? (
                      "Try again"
                    ) : (
                      copyLabel
                    )}
                  </button>
                ) : (
                  <a
                    className="_co_onb_finish _co_home_onb_primary"
                    data-testid="template-copy-prompt-primary"
                    {...({ alt: "template-prompt-view-file" } as Record<
                      string,
                      string
                    >)}
                    href={buildPublicFileUrl(prompt.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleView}
                  >
                    {viewLabel}
                  </a>
                )}

                {isUnavailable ? null : (
                  <button
                    type="button"
                    className="_co_home_onb_secondary"
                    data-testid="template-copy-prompt-secondary"
                    {...({ alt: "template-prompt-maybe-later" } as Record<
                      string,
                      string
                    >)}
                    onClick={context.dismiss}
                    disabled={isCopying}
                  >
                    Maybe later
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body,
    );
  },
);

const Spinner = memo(function Spinner() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
      className="_co_home_onb_spinner"
    >
      <circle
        cx="7"
        cy="7"
        r="5.25"
        stroke="currentColor"
        strokeOpacity="0.3"
        strokeWidth="1.6"
      />
      <path
        d="M12.25 7a5.25 5.25 0 0 0-5.25-5.25"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
});

const DIALOG_STYLES = `
  ._co_home_onb_scrim {
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: rgba(15, 12, 8, 0.42);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    animation: _co_home_onb_scrim_in 240ms cubic-bezier(0.2, 0, 0, 1) both;
  }
  .dark ._co_home_onb_scrim {
    background: rgba(0, 0, 0, 0.62);
  }
  @keyframes _co_home_onb_scrim_in {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  ._co_home_onb_card {
    --co-onb-fs-eyebrow: 12px;
    --co-onb-fs-heading: 30px;
    --co-onb-fs-lede: 16px;
    --co-onb-fs-cta: 14px;
    --co-onb-stack-sm: 12px;
    --co-onb-stack-md: 16px;
    --co-onb-pad-x: 40px;
    --co-onb-pad-y: 32px;
    --co-onb-content-max: 520px;
    --co-onb-dot: 9px;

    position: relative;
    width: min(560px, 100%);
    max-height: calc(100vh - 48px);
    overflow: hidden;
    background: var(--co-paper);
    color: var(--co-fg);
    border: 1px solid var(--co-border);
    border-radius: 22px;
    box-shadow:
      0 1px 2px rgba(15, 12, 8, 0.06),
      0 28px 80px rgba(15, 12, 8, 0.28);
    font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Inter', 'Geist', sans-serif;
    animation: _co_home_onb_card_in 420ms cubic-bezier(0.2, 0, 0, 1) both;
  }
  .dark ._co_home_onb_card {
    background: rgba(26, 26, 22, 0.98);
    box-shadow:
      0 1px 2px rgba(0, 0, 0, 0.4),
      0 32px 96px rgba(0, 0, 0, 0.6);
  }
  @keyframes _co_home_onb_card_in {
    from { opacity: 0; transform: translateY(10px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0)    scale(1); }
  }

  ._co_tpl_prompt_preview {
    position: relative;
    aspect-ratio: 1200 / 630;
    background: var(--co-paper-2, #F5F0E5);
    border-bottom: 1px solid var(--co-border);
    overflow: hidden;
  }
  ._co_tpl_prompt_preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  ._co_tpl_prompt_preview_fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(to bottom right, #F5F0E5, #EBE3D1);
  }
  .dark ._co_tpl_prompt_preview_fallback {
    background: linear-gradient(to bottom right, #221f19, #19160f);
  }

  ._co_home_onb_content {
    padding: var(--co-onb-pad-y) var(--co-onb-pad-x);
    display: flex;
    flex-direction: column;
    gap: var(--co-onb-stack-md);
    align-items: stretch;
    text-align: left;
  }

  ._co_home_onb_eyebrow {
    font-family: ui-monospace, 'JetBrains Mono', 'SF Mono', Menlo, monospace;
    font-size: var(--co-onb-fs-eyebrow);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--co-fg-muted);
    display: inline-flex;
    align-items: center;
    gap: var(--co-onb-stack-sm);
  }

  ._co_home_onb_heading {
    margin: 0;
    max-width: var(--co-onb-content-max);
    font-size: var(--co-onb-fs-heading);
    line-height: 1.15;
    letter-spacing: -0.02em;
    font-weight: 500;
    color: var(--co-fg);
  }

  ._co_tpl_prompt_author {
    margin: calc(-1 * var(--co-onb-stack-sm)) 0 0;
    font-size: 14px;
    color: var(--co-fg-muted);
  }

  ._co_home_onb_lede {
    margin: 0;
    max-width: var(--co-onb-content-max);
    font-size: var(--co-onb-fs-lede);
    line-height: 1.5;
    color: var(--co-fg-muted);
    font-weight: 400;
  }

  ._co_tpl_prompt_error {
    margin: 0;
    font-size: 14px;
    line-height: 1.4;
    color: var(--co-rust);
  }

  ._co_home_onb_actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 14px;
    margin-top: var(--co-onb-stack-sm);
  }

  a._co_home_onb_primary {
    text-decoration: none;
  }

  ._co_home_onb_primary[disabled] {
    opacity: 0.7;
    cursor: progress;
    scale: 1 !important;
  }

  ._co_home_onb_secondary {
    appearance: none;
    background: transparent;
    border: none;
    padding: 0 8px;
    margin: 0;
    min-height: 44px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    color: var(--co-fg-muted);
    cursor: pointer;
    border-radius: 8px;
    transition: color 160ms cubic-bezier(0.2, 0, 0, 1);
  }
  ._co_home_onb_secondary:hover { color: var(--co-fg); }
  ._co_home_onb_secondary:focus-visible {
    outline: 2px solid var(--co-rust);
    outline-offset: 2px;
  }
  ._co_home_onb_secondary[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @keyframes _co_home_onb_spin {
    to { transform: rotate(360deg); }
  }
  ._co_home_onb_spinner {
    animation: _co_home_onb_spin 700ms linear infinite;
    transform-origin: 50% 50%;
  }

  @media (max-width: 520px) {
    ._co_home_onb_card {
      --co-onb-fs-heading: 26px;
      --co-onb-pad-x: 24px;
      --co-onb-pad-y: 24px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    ._co_home_onb_scrim,
    ._co_home_onb_card,
    ._co_home_onb_spinner { animation: none !important; }
  }
`;
